/**
 * Utility to deterministically calculate payroll periods for any given year
 * based purely on a one-time anchor start date.
 * 
 * @param {string} anchorStartDate - The YYYY-MM-DD of the first ever pay cycle start
 * @param {number} targetYear - The year you want to view (e.g., 2026)
 * @param {string} frequency - 'weekly', 'biweekly', 'semi-monthly', 'monthly'
 * @returns {Array} List of period objects for the target year
 */
export const calculatePeriodsForYear = (anchorStartDate, targetYear, frequency) => {
  if (!anchorStartDate) return [];

  let currentStart = new Date(`${anchorStartDate}T00:00:00Z`);
  let periodIndex = 1;
  let lastEndYear = currentStart.getUTCFullYear(); // Track the year to reset sequences cleanly
  const results = [];
  
  // Hardcoded to prevent timezone shift bugs from toLocaleString()
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  // Loop forward from the dawn of time (anchor date) until we pass the target year
  while (true) {
    let currentEnd = new Date(currentStart);
    let nextStart = new Date(currentStart.getTime());
    let payDate;

    if (frequency === 'monthly') {
      currentEnd = new Date(Date.UTC(currentStart.getUTCFullYear(), currentStart.getUTCMonth() + 1, 0));
      payDate = new Date(currentEnd);
      payDate.setUTCDate(currentEnd.getUTCDate() + 3);
      nextStart.setUTCDate(1); 
      nextStart.setUTCMonth(currentStart.getUTCMonth() + 1);
    } else if (frequency === 'semi-monthly') {
      if (currentStart.getUTCDate() === 1) {
        currentEnd.setUTCDate(15);
        payDate = new Date(currentEnd);
        payDate.setUTCDate(currentEnd.getUTCDate() + 3);
        nextStart.setUTCDate(16);
      } else {
        currentEnd = new Date(Date.UTC(currentStart.getUTCFullYear(), currentStart.getUTCMonth() + 1, 0)); 
        payDate = new Date(currentEnd);
        payDate.setUTCDate(currentEnd.getUTCDate() + 3);
        nextStart.setUTCDate(1);
        nextStart.setUTCMonth(currentStart.getUTCMonth() + 1);
      }
    } 
    else if (frequency === 'biweekly') {
      currentEnd.setUTCDate(currentStart.getUTCDate() + 13);
      payDate = new Date(currentEnd); 
      payDate.setUTCDate(currentEnd.getUTCDate() + 3);
      nextStart.setUTCDate(currentStart.getUTCDate() + 14);
    } 
    else if (frequency === 'weekly') {
      currentEnd.setUTCDate(currentStart.getUTCDate() + 6);
      payDate = new Date(currentEnd);
      payDate.setUTCDate(currentEnd.getUTCDate() + 3);
      nextStart.setUTCDate(currentStart.getUTCDate() + 7);
    }

    const endYear = currentEnd.getUTCFullYear();

    // Check if we crossed into a new calendar year based on the END DATE
    if (endYear > lastEndYear) {
      periodIndex = 1; // Reset to Period 1 for the new year
      lastEndYear = endYear;
    }

    // Only collect the periods that belong to the requested TARGET YEAR
    // We strictly use the endYear to determine tax year grouping
    if (endYear === targetYear) {
      const monthLabel = monthNames[currentEnd.getUTCMonth()];
      const year = endYear;
      const month = String(currentEnd.getUTCMonth() + 1).padStart(2, '0');
      const day = String(currentEnd.getUTCDate()).padStart(2, '0');
      const periodId = `${frequency}-${year}${month}${day}`;

      results.push({
        id: periodId,
        period_number: periodIndex,
        period_name: `${monthLabel} Period ${periodIndex}`,
        pay_frequency: frequency,
        start_date: currentStart.toISOString().split('T')[0],
        end_date: currentEnd.toISOString().split('T')[0],
        pay_date: payDate.toISOString().split('T')[0],
      });
    }

    // Break the loop once we've calculated past the target year
    if (endYear > targetYear) {
      break;
    }

    currentStart = nextStart;
    periodIndex++;
  }

  return results;
};